import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  InputNumber,
  message,
  Popconfirm,
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

interface VipLevel {
  id: number;
  level: number;
  levelName: string;
  rechargeAmount: number;
  promotionGift: number;
  weeklySalary: number;
  monthlySalary: number;
}

// Demo数据 - 11个VIP等级（VIP0-VIP10）
const initialVipLevels: VipLevel[] = Array.from({ length: 11 }, (_, i) => ({
  id: i + 1,
  level: i,
  levelName: `VIP${i}`,
  rechargeAmount: i === 0 ? 0 : i * 50000,
  promotionGift: i === 0 ? 0 : i * 1000,
  weeklySalary: i === 0 ? 0 : i * 1000,
  monthlySalary: i === 0 ? 0 : i * 100,
}));

const VipLevelConfig: React.FC = () => {
  const [vipLevels, setVipLevels] = useState<VipLevel[]>(initialVipLevels);
  const [editingData, setEditingData] = useState<Record<number, Partial<VipLevel>>>({});

  const handleFieldChange = (id: number, field: keyof VipLevel, value: any) => {
    setEditingData(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    const updatedLevels = vipLevels.map(level => ({
      ...level,
      ...editingData[level.id],
    }));
    setVipLevels(updatedLevels);
    setEditingData({});
    message.success('VIP等级配置已保存');
  };

  const handleReset = () => {
    setVipLevels(initialVipLevels);
    setEditingData({});
    message.info('已重置为初始数据');
  };

  const handleDelete = (id: number) => {
    setVipLevels(prev => prev.filter(item => item.id !== id));
    message.success('已删除该VIP等级');
  };

  const handleAdd = () => {
    const newLevel: VipLevel = {
      id: Date.now(),
      level: vipLevels.length,
      levelName: `VIP${vipLevels.length}`,
      rechargeAmount: 0,
      promotionGift: 0,
      weeklySalary: 0,
      monthlySalary: 0,
    };
    setVipLevels(prev => [...prev, newLevel]);
    message.success('已添加新VIP等级');
  };

  const columns = [
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      align: 'center' as const,
      render: (level: number, record: VipLevel) => (
        <InputNumber
          value={editingData[record.id]?.level ?? level}
          onChange={(value) => handleFieldChange(record.id, 'level', value ?? 0)}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '等级名称',
      dataIndex: 'levelName',
      key: 'levelName',
      width: 150,
      render: (levelName: string, record: VipLevel) => (
        <Input
          value={editingData[record.id]?.levelName ?? levelName}
          onChange={(e) => handleFieldChange(record.id, 'levelName', e.target.value)}
          placeholder="请输入等级名称"
        />
      ),
    },
    {
      title: '充值金额',
      dataIndex: 'rechargeAmount',
      key: 'rechargeAmount',
      width: 150,
      render: (rechargeAmount: number, record: VipLevel) => (
        <InputNumber
          value={editingData[record.id]?.rechargeAmount ?? rechargeAmount}
          onChange={(value) => handleFieldChange(record.id, 'rechargeAmount', value ?? 0)}
          min={0}
          style={{ width: '100%' }}
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        />
      ),
    },
    {
      title: '晋级礼金',
      dataIndex: 'promotionGift',
      key: 'promotionGift',
      width: 150,
      render: (promotionGift: number, record: VipLevel) => (
        <InputNumber
          value={editingData[record.id]?.promotionGift ?? promotionGift}
          onChange={(value) => handleFieldChange(record.id, 'promotionGift', value ?? 0)}
          min={0}
          style={{ width: '100%' }}
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        />
      ),
    },
    {
      title: '周俸禄',
      dataIndex: 'weeklySalary',
      key: 'weeklySalary',
      width: 150,
      render: (weeklySalary: number, record: VipLevel) => (
        <InputNumber
          value={editingData[record.id]?.weeklySalary ?? weeklySalary}
          onChange={(value) => handleFieldChange(record.id, 'weeklySalary', value ?? 0)}
          min={0}
          style={{ width: '100%' }}
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        />
      ),
    },
    {
      title: '月俸禄',
      dataIndex: 'monthlySalary',
      key: 'monthlySalary',
      width: 150,
      render: (monthlySalary: number, record: VipLevel) => (
        <InputNumber
          value={editingData[record.id]?.monthlySalary ?? monthlySalary}
          onChange={(value) => handleFieldChange(record.id, 'monthlySalary', value ?? 0)}
          min={0}
          style={{ width: '100%' }}
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: VipLevel) => (
        <Popconfirm
          title="确认删除该VIP等级？"
          onConfirm={() => handleDelete(record.id)}
        >
          <Button type="link" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="page-header">
        <h1>VIP等级配置</h1>
        <p>配置VIP等级信息，包括充值金额、晋级礼金、周俸禄、月俸禄等</p>
      </div>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => message.info('缓存查询成功')}>
            缓存查询
          </Button>
          <Button onClick={handleReset}>
            重置
          </Button>
          <Button danger onClick={() => message.success('缓存已清除')}>
            清除
          </Button>
        </Space>

        <Table
          rowKey="id"
          dataSource={vipLevels}
          columns={columns}
          pagination={false}
          scroll={{ x: 1000 }}
          bordered
        />

        <Space style={{ marginTop: 16 }}>
          <Button onClick={handleReset}>
            重置
          </Button>
          <Button type="primary" onClick={handleSave}>
            确认
          </Button>
        </Space>
      </Card>
    </div>
  );
};

export default VipLevelConfig;


